import React from "react";
import {
	Button,
	ClassNameProps,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	StyleProps,
	useCount,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { useRecordViewOnMount } from "../../../../Utilities";
import { MediaAttachmentUtilities } from "../../../Utilities";
import { getPreloadMediaAttachments } from "../../MediaAttachment";
import { DetailDisplayPost, PostDetailDisplay, PostVisualAttachmentDetailDisplay } from "../DetailDisplay";
import styles from "./styles.module.css";

export type ModalPost = DetailDisplayPost;

export type PostModalProps = {
	post: ModalPost;
	initialIndex?: number;
	withBackButton?: boolean;
	backButtonText?: string;
	noReactions?: boolean;
	noComments?: boolean;
	isPreview?: boolean;
} & Pick<ModalProps, "ifRef" | "onClose"> &
	Partial<ClassNameProps & StyleProps>;

export function PostModal({ post, initialIndex, ...props }: PostModalProps) {
	useRecordViewOnMount(post.id);

	if (initialIndex !== undefined) {
		return <PostVisualAttachmentModal post={post} initialIndex={initialIndex} {...props} />;
	}
	return <PostOverviewModal post={post} {...props} />;
}

export function PostOverviewModal({
	post,
	withBackButton = false,
	backButtonText = "Back to Timeline",
	noReactions = false,
	noComments = false,
	isPreview = false,
	ifRef,
	onClose,
	className,
	style,
}: Omit<PostModalProps, "initialIndex">) {
	const {
		isModalOpen: isVisualAttachmentModalOpen,
		openModal: openVisualAttachmentModal,
		closeModal: closeVisualAttachmentModal,
	} = Modal.useToggle();
	const [modalVisualAttachmentIndex, setModalVisualAttachmentIndex] = React.useState(0);
	const onClickVisualAttachment = React.useCallback(
		(index: number) => {
			setModalVisualAttachmentIndex(index);
			openVisualAttachmentModal();
		},
		[openVisualAttachmentModal],
	);

	return (
		<Modal
			ifRef={ifRef}
			size="large"
			onClose={onClose}
			className={makeClassName(styles["post-modal"], className)}
			style={style}
		>
			<If condition={withBackButton}>
				<Modal.Header>
					{onClose && (
						<Button variant="link" size="small" onClick={onClose}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
								<Icon.ArrowLeft size={14} /> {backButtonText}
							</Row>
						</Button>
					)}
				</Modal.Header>
			</If>
			<Modal.Body>
				<PostDetailDisplay
					post={post}
					onClickVisualAttachment={onClickVisualAttachment}
					withReactions={!noReactions}
					withComments={!noComments}
					editable
					isPreview={isPreview}
				/>

				<Modal.If condition={isVisualAttachmentModalOpen}>
					<PostVisualAttachmentModal
						post={post}
						initialIndex={modalVisualAttachmentIndex}
						noReactions={noReactions}
						noComments={noComments}
						onClose={closeVisualAttachmentModal}
					/>
				</Modal.If>
			</Modal.Body>
		</Modal>
	);
}

export function PostVisualAttachmentModal({
	post,
	initialIndex = 0,
	noReactions = false,
	noComments = false,
	ifRef,
	onClose,
	className,
	style,
}: PostModalProps) {
	const { visualAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitMediaAttachments(post.mediaAttachments),
		[post.mediaAttachments],
	);

	const {
		count: index,
		decrement: decrementIndex,
		increment: incrementIndex,
	} = useCount(initialIndex, { mod: visualAttachments.length });
	const attachment = visualAttachments[index];

	const preloadAttachments = React.useMemo(
		() => getPreloadMediaAttachments(visualAttachments, index),
		[visualAttachments, index],
	);

	useRecordViewOnMount(attachment.id);

	return (
		<Modal ifRef={ifRef} size="large" onClose={onClose} className={className} style={style}>
			<Modal.Body style={{ height: "100%" }}>
				<PostVisualAttachmentDetailDisplay
					post={post}
					index={index}
					mediaAttachment={attachment}
					preloadMediaAttachments={preloadAttachments}
					totalAttachments={visualAttachments.length}
					incrementIndex={incrementIndex}
					decrementIndex={decrementIndex}
					withReactions={!noReactions}
					withComments={!noComments}
					style={{ height: "100%" }}
				/>
			</Modal.Body>
		</Modal>
	);
}
